import { useMemo, useState } from 'react';
import { Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { css, Theme, useTheme } from '@emotion/react';
import { SignOut, User, Users, Gear, Plus } from '@phosphor-icons/react';
import { AccessRights } from 'common/account/AccessRights';
import { AccountUserRole } from 'common/CommonConstants';
import { getPlaceholderUrl } from 'common/commonUtil';
import { useUser } from 'client/lib/User';
import { Button } from 'client/components/Button';
import { ButtonWithTrialBlock } from 'client/components/Button/ButtonWithTrialBlock';
import { Dashboard, Sidebar, Content } from 'client/components/Layout/Dashboard';
import { MenuList } from 'client/components/Menu';
import type { MenuItemProps } from 'client/components/Menu';
import { adminSubtitle, memberSubtitle } from 'client/components/Other/AccountSelect';
import { ListSubheader } from 'client/components/Other/ListSubheader';
import { Subheader } from 'client/components/Subheader';
import { AccountTab } from './AccountTab';
import { AccountCreateBlockDialog } from './components/AccountCreateBlockDialog';
import { CreateAccountDialog } from './components/CreateAccountDialog';
import { ProfileTab } from './ProfileTab';

export const ProfilePage = () => {
  const params = useParams();
  const theme = useTheme();
  const navigate = useNavigate();
  const { user: actualUser, logout, hasAccessRight } = useUser();
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);

  // FIXME: Do not allow "trial" users to create new accounts
  const isTrial = hasAccessRight(AccessRights.TRIAL_USER);
  const isAnonymousTrial = actualUser && !!actualUser.anonymousTrial;

  const user = { ...actualUser };
  if (isAnonymousTrial) {
    user.firstName = 'Profile';
    user.lastName = '';
    user.accounts = (user.accounts || []).map((account) => ({ ...account, name: 'Account' }));
  }

  const accountID = params['*'] || null;

  const handleAccountCreate = (accountID: number) => {
    navigate(`/settings/${accountID}`);
    setAccountDialogOpen(false);
  };

  const profileItem = useMemo<MenuItemProps>(
    () => ({
      id: 'profile',
      title: [user?.firstName, user?.lastName].filter((item) => !!item).join(' ') || 'Profile',
      subtitle: 'Settings for your personal profile',
      image: {
        alt: 'profile picture',
        src: user?.githubUsername ? `https://github.com/${user.githubUsername}.png?size=32` : getPlaceholderUrl('user', user?.userID || 0),
        shape: 'circle',
      },
      href: '/settings',
      selected: !accountID,
    }),
    [accountID, user]
  );

  const accountItems = useMemo<MenuItemProps[]>(
    () =>
      (user?.accounts || []).map((account) => ({
        id: `${account.id}`,
        title: account.name,
        subtitle: account.accountUserRoleID === AccountUserRole.ADMINISTRATOR ? adminSubtitle(theme) : memberSubtitle,
        image: { alt: 'account profile picture', src: getPlaceholderUrl('account', account.id), shape: 'square' },
        href: `/settings/${account.id}`,
        selected: accountID === `${account.id}`,
      })),
    [accountID, user?.accounts, theme]
  );

  const menuItems = useMemo(
    () => [
      <ListSubheader id="profile-header">
        <User /> Profile
      </ListSubheader>,
      profileItem,
      <ListSubheader id="accounts">
        <Users /> Accounts
      </ListSubheader>,
      ...accountItems,
    ],
    [profileItem, accountItems]
  );

  return (
    <>
      <Subheader
        title="Settings"
        subtitle="Edit profile & account settings"
        icon={<Gear />}
        css={subheaderStyle}
        actions={
          <Button icon={<SignOut weight="fill" />} onClick={() => logout()}>
            Sign Out
          </Button>
        }
      />

      <Dashboard>
        <Sidebar css={sidebarStyle}>
          <MenuList items={menuItems} css={menuStyle} />
          <div css={actionsStyle}>
            <ButtonWithTrialBlock icon={<Plus weight="bold" />} onClick={() => setAccountDialogOpen(true)}>
              Create Account
            </ButtonWithTrialBlock>
          </div>
        </Sidebar>
        <Content>
          <Routes>
            <Route index element={<ProfileTab />} />
            <Route path="/:accountID" element={<AccountTab />} />
          </Routes>
        </Content>
      </Dashboard>

      {!isTrial && (
        <CreateAccountDialog open={accountDialogOpen} onCreate={handleAccountCreate} onClose={() => setAccountDialogOpen(false)} />
      )}
      {isTrial && <AccountCreateBlockDialog open={accountDialogOpen} onClose={() => setAccountDialogOpen(false)} />}
    </>
  );
};

const subheaderStyle = css`
  header > div > h4 > img {
    border-radius: 50%;
  }
`;

const menuStyle = css`
  padding: 0;

  > li:first-of-type .MuiListItemSecondaryAction-root {
    display: none;
  }
`;

const sidebarStyle = (theme: Theme) => css`
  margin-left: 0 !important;
  margin-right: 64px;
  padding-right: 64px;
  border-right: 2px solid ${theme.color.grey[60]};

  box-sizing: border-box;

  .MuiMenuItem-root.Mui-selected {
    background-color: ${theme.color.grey[60]};
  }

  .MuiListSubheader-root {
    background-color: ${theme.color.background};
    color: ${theme.color.grey[100]};
  }
`;

const actionsStyle = css`
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
`;
